import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock, BarChart3, Users, Zap, Shield, Target, Monitor, Smartphone, Download, BookOpen, Award } from 'lucide-react';
import { api, API_BASE_URL } from '../../api';

interface Feature {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  players: string;
  engagement: string;
  features: string[];
  popular: boolean;
}

const ShowcaseLogiciel: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const androidDownloadUrl = `${API_BASE_URL}/api/downloads/android`;
  const iosDownloadUrl = `${API_BASE_URL}/api/downloads/ios`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuresResponse, plansResponse] = await Promise.all([
          api.get('/siteContent/features'),
          api.get('/siteContent/plans'),
        ]);
        setFeatures(featuresResponse.data || []);
        setPlans(plansResponse.data || []);
      } catch (err) {
        setError('Failed to fetch software content.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'BarChart3': return BarChart3;
      case 'Users': return Users;
      case 'Target': return Target;
      case 'Shield': return Shield;
      case 'Clock': return Clock;
      case 'Zap': return Zap;
      case 'BookOpen': return BookOpen;
      case 'CheckCircle': return CheckCircle;
      case 'Award': return Award;
      default: return BarChart3; // Default icon
    }
  };

  if (loading) {
    return <div>Loading software content...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  L'<span className="text-[#FFB639]">application</span> FORMACADEMY
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Le LMS mobile de FormAcademy : suivez vos formations où que vous soyez, avec cours, quiz et certificats directement dans l'application.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Essai gratuit 14 jours
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
                  <Monitor className="mr-2 h-5 w-5" />
                  Voir la démo
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
                <a
                  href={androidDownloadUrl}
                  download="formacademy-android.apk"
                  className="inline-flex items-center justify-center px-6 py-4 bg-white/10 border border-white/30 text-white rounded-lg backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
                >
                  <Smartphone className="mr-3 h-5 w-5" />
                  Télécharger l'app Android
                  <Download className="ml-3 h-5 w-5" />
                </a>
                <a
                  href={iosDownloadUrl}
                  download="formacademy-ios-app.ipa"
                  className="inline-flex items-center justify-center px-6 py-4 bg-white/10 border border-white/30 text-white rounded-lg backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
                >
                  <Smartphone className="mr-3 h-5 w-5" />
                  Télécharger l'app iOS
                  <Download className="ml-3 h-5 w-5" />
                </a>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Sans engagement</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Support inclus</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg"
                  alt="Interface application FORMACADEMY"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm">Apprenez à votre rythme</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une application complète pour suivre vos formations, tester vos connaissances et obtenir vos certificats
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = getIconComponent(feature.icon);
              return (
                <div key={feature.id} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-12 h-12 bg-[#FFB639]/15 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-[#FFB639]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Interface intuitive
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez l'interface pensée pour les apprenants
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  Votre progression en temps réel
                </h3>
                <p className="text-lg text-gray-600">
                  Suivez votre avancement module par module : scores de quiz, temps d'étude et certificats obtenus.
                </p>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Progression en direct</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Rappels de cours personnalisés</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Reprenez où vous en étiez</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Certificats téléchargeables</span>
                </li>
              </ul>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg"
                alt="Espace apprenant FORMACADEMY"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg"
                alt="Application mobile FORMACADEMY"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -top-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <Smartphone className="h-8 w-8 text-[#FFB639]" />
                  <div>
                    <div className="font-semibold text-gray-900">App Mobile</div>
                    <div className="text-sm text-gray-600">iOS & Android</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  Toute votre formation dans votre poche
                </h3>
                <p className="text-lg text-gray-600">
                  Suivez vos cours depuis votre smartphone, même hors connexion, grâce à notre application native iOS et Android.
                </p>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Synchronisation en temps réel</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Mode hors ligne</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Notifications push</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Interface optimisée mobile</span>
                </li>
              </ul>

              <div className="flex space-x-4">
                <a
                  href={iosDownloadUrl}
                  download="formacademy-ios-app.ipa"
                  className="flex items-center px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <span className="mr-2">📱</span>
                  Télécharger iOS
                </a>
                <a
                  href={androidDownloadUrl}
                  download="formacademy-android.apk"
                  className="flex items-center px-6 py-3 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                >
                  <span className="mr-2">🤖</span>
                  Télécharger Android
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Tarifs transparents
            </h2>
            <p className="text-xl text-gray-600">
              Choisissez l'offre qui correspond à la taille de votre structure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div key={plan.id} className={`bg-white rounded-2xl shadow-lg overflow-hidden ${plan.popular ? 'ring-2 ring-[#FFB639] transform scale-105' : ''}`}>
                {plan.popular && (
                  <div className="bg-[#FFB639] text-white text-center py-2 text-sm font-semibold">
                    Le plus populaire
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <p className="text-gray-600 mb-2">{plan.players}</p>
                  <p className="text-sm text-gray-500 mb-6">Engagement {plan.engagement}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#FFB639] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to="/login"
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-300 text-center block ${
                      plan.popular
                        ? 'bg-[#FFB639] text-white hover:bg-[#FFB639]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Commencer l'essai
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Besoin d'une solution sur mesure pour votre organisme ?
            </p>
            <button className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-300">
              Contactez notre équipe
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Prêt à révolutionner votre façon de vous former ?
          </h2>
          <p className="text-xl text-[#FFB639] mb-8 max-w-3xl mx-auto">
            Rejoignez les centaines de professionnels qui utilisent déjà FORMACADEMY pour optimiser leurs performances
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105"
            >
              Essai gratuit 14 jours
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
              Demander une démo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseLogiciel;
